import React, { useState, useEffect, useRef, useMemo } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "@src/../entrypoints/theme";
import BannerOverlay from "./BannerOverlay/BannerOverlay";
import "./Banner.scss";

import BannerImage from "./Image/BannerImage";
import { sendGtagEvent } from "frontend/js/helpers";
import { useImagePreloader } from "@project-stories/elements/Image/ImagePreloader";

let hasLoaded = false;

// Helper function to extract Vimeo video ID from URL
const extractVimeoId = (url: string): string | null => {
  if (!url) return null;
  
  const regex = /(?:vimeo\.com\/(?:.*#|.*\/videos\/|.*\/)?([0-9]+))/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

// Helper function to create Vimeo embed URL
const createVimeoEmbedUrl = (videoId: string, quality: string, muted: boolean = true): string => {
  return `https://player.vimeo.com/video/${videoId}?autoplay=1&quality=${quality}p&loop=1&autopause=0&background=1&muted=${muted ? 1 : 0}`;
};

const normalizeHttpsUrl = (url: string = ""): string => {
  if (url.startsWith("//")) {
    return `https:${url}`;
  }

  return url;
};

export const Banner = ({
  banners,
  useCarousel = true,
  section_settings,
  props,
}) => {

  const sectionId = props.section_id;
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const { globals } = props;

  const [videoPlaying, setVideoPlaying] = useState(
      Array(banners.length).fill(false),
  );
  const [videoMuted, setVideoMuted] = useState(
      Array(banners.length).fill(true),
  );
  const videoRefs = useRef([]);

  // Preload critical banner images (or video poster) for better LCP
  const criticalImages = useMemo(() => {
      if (!section_settings.is_primary) return [];

      const firstBanner = banners[0];
      if (!firstBanner) return [];

      if (firstBanner.type === "block_image") {
        const imageUrl = isMobile
          ? (firstBanner.settings.mobile_image || firstBanner.settings.image?.src || firstBanner.settings.image)
          : (firstBanner.settings.image?.src || firstBanner.settings.image);

        return imageUrl ? [{ url: imageUrl, priority: 10 }] : [];
      }

      if (firstBanner.type === "block_video") {
        const poster = isMobile
          ? (firstBanner.settings.video_mobile?.preview_image?.src || firstBanner.settings.video?.preview_image?.src || "")
          : (firstBanner.settings.video?.preview_image?.src || "");

        return poster ? [{ url: poster, priority: 10 }] : [];
      }

      return [];
    }, [banners, isMobile, section_settings.is_primary]);

  useImagePreloader(criticalImages);

  let showButtons = section_settings.show_buttons;
  if (isMobile) {
    showButtons = section_settings.show_buttons_mobile;
  }

  let showDots = section_settings.show_indicators;
  if (isMobile) {
    showDots = section_settings.show_indicators_mobile;
  }

  let autoPlay: any = false;

  if (section_settings.auto_scroll) {
    if (section_settings.scroll_timer) {
      autoPlay = section_settings.scroll_timer * 1000;
    }
  }

 const videoSourcesArray = useMemo(() => {
    return banners.map((blockItem) => {
      if (blockItem.type === "block_video") {
        // Check for Vimeo URLs first (prioritize them)
        const vimeoUrl = isMobile 
          ? blockItem.settings.vimeo_video_url_mobile || blockItem.settings.vimeo_video_url
          : blockItem.settings.vimeo_video_url;
        
        if (vimeoUrl) {
          const vimeoId = extractVimeoId(vimeoUrl);
          if (vimeoId) {
            const videoUrl = createVimeoEmbedUrl(vimeoId, globals.video_quality);
            const posterSettings =
              isMobile && blockItem.settings.video_mobile
                ? blockItem.settings.video_mobile
                : blockItem.settings.video;

            let videoPoster = normalizeHttpsUrl(
              posterSettings?.preview_image?.src || "",
            );
            
            if (videoPoster) {
              videoPoster += isMobile ? "&format=avif&width=750" : "&format=avif&width=1920";
            }
            
            return { videoUrl, videoPoster, isVimeo: true };
          }
        }
        
        // Fall back to Shopify video settings
        // On mobile, try video_mobile first, fallback to video if not set
        const videoSettings = (isMobile && blockItem.settings.video_mobile)
            ? blockItem.settings.video_mobile
            : blockItem.settings.video;

        if (videoSettings?.sources) {
          const sources = videoSettings.sources;
          const quality = parseInt(globals.video_quality);
          let highestResolution = 0;
          let highestSource = '';
          let fallbackSource = '';
          let exactMatchFound = false;

          sources.forEach((source) => {
            if (source.format === 'mp4') {
              if (source.height === quality) {
                highestSource = source.url;
                exactMatchFound = true;
              }
              if (!exactMatchFound && source.height > highestResolution) {
                highestResolution = source.height;
                fallbackSource = source.url;
              }
            }
          });

          const videoUrl = exactMatchFound ? highestSource : fallbackSource;
          let videoPoster = normalizeHttpsUrl(
            videoSettings.preview_image?.src || "",
          );
          
          if (videoPoster) {
            videoPoster += isMobile ? "&format=avif&width=750" : "&format=avif&width=1920";
          }
          
          return { videoUrl, videoPoster, isVimeo: false };
        }
      }
      return null;
    });
  }, [banners, isMobile, globals.video_quality]);

  const handleCarouselChange = (e) => {
    if (e.detail.sectionId === sectionId) {
      const blockItem = banners[e.detail.activeIndex]

      sendGtagEvent(
        'view_promotion',
        blockItem?.id,
        blockItem?.settings?.promotional_name,
        'banner'
      )
    }
  }

  useEffect(() => {
    if (hasLoaded) return

    hasLoaded = true;

    // initial event
    handleCarouselChange({
      detail: {
        sectionId,
        activeIndex: 0
      }
    });

    window.addEventListener('carousel-change', handleCarouselChange);

    return () => {
      window.removeEventListener('carousel-change', handleCarouselChange)
    }
  }, [sectionId]);


  // Track single_banner when it enters viewport
  useEffect(() => {
    if (!useCarousel && banners.length > 0) {
      const bannerElement = document.querySelector(`.banner[data-section-id="${sectionId}"]`);

      if (!bannerElement) return;

      const observer = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              banners.forEach((blockItem) => {
                sendGtagEvent(
                  'view_promotion',
                  blockItem?.id,
                  blockItem?.settings?.promotional_name,
                  'single_banner'
                );
              });
              observer.disconnect(); // fire only once
            }
          });
        },
        { threshold: 0.5 }
      );

      observer.observe(bannerElement);

      return () => observer.disconnect();
    }
  }, [useCarousel, banners, sectionId]);

  useEffect(() => {
    banners.forEach((blockItem, index) => {
      if (blockItem.type === "block_video" && videoRefs.current[index]) {
        const videoElement = document.querySelector(`#video-${index}`);
        videoRefs.current[index] = videoElement;
      }
    });
  }, [banners]);

  const handlePlayButtonClick = (index) => {
    const updatedVideoPlaying = [...videoPlaying];
    updatedVideoPlaying[index] = true;
    setVideoPlaying(updatedVideoPlaying);

    const videoElement: any = videoRefs.current[index];
    if (videoElement) {
      videoElement.controls = false;
      videoElement.play();
    }
  };

  const handleSoundToggle = (index) => {
    // console.log("Toggling sound for video index:", index);
    const updatedVideoMuted = [...videoMuted];
    updatedVideoMuted[index] = !updatedVideoMuted[index];
    setVideoMuted(updatedVideoMuted);

    const isVimeo = videoSourcesArray[index]?.isVimeo;

    if (!isVimeo) return;

    const vimeoUrl = isMobile 
      ? banners[index].settings.vimeo_video_url_mobile || banners[index].settings.vimeo_video_url
      : banners[index].settings.vimeo_video_url;

    if (vimeoUrl) {
      const vimeoId = extractVimeoId(vimeoUrl);
      if (vimeoId) {
        // Use the NEW muted state (updatedVideoMuted) instead of the old state
        const newVideoUrl = createVimeoEmbedUrl(vimeoId, globals.video_quality, updatedVideoMuted[index]);
        // console.log("New Vimeo URL:", newVideoUrl, "Muted:", updatedVideoMuted[index]);
        const iframe = document.getElementById(`vimeo-${index}`) as HTMLIFrameElement;
        if (iframe) {
          iframe.src = newVideoUrl;
        }
      }
    }
  };

  const height = section_settings.height;
  const heightClass = `${ height > 0 ? `lg:h-[${height}px]` : `lg:h-screen ${section_settings.is_primary && "lg:min-h-screen"}`}`;

  function handleClick (e, blockItem) {
    e.preventDefault();

    sendGtagEvent(
      'select_promotion',
      blockItem?.id,
      blockItem?.settings?.promotional_name,
      'banner',
      blockItem?.settings?.button_url
    );
  }

  function handleSecondButtonClick(e, blockItem) {
    e.preventDefault();
    sendGtagEvent(
      'select_promotion',
      blockItem?.id,
      blockItem?.settings?.promotional_name,
      'banner_second_button',
      blockItem?.settings?.button_url_2
    );
  }

  return (
    <div
      className={`banner ${heightClass} h-[${section_settings.mobile_height}px]  w-full`}
      data-section-id={sectionId}
      style={{
        minHeight: height > 0 ? 'auto' : 'initial',
      }}
    >
      <div
        className={"banner-carousel w-full h-full"}
        {...(useCarousel && {
          "x-data": "carousel",
          "x-init": `$nextTick(() => { initialise(${showDots}, ${showButtons}, false, false, ${autoPlay}, '${sectionId}', false) })`,
          "x-ref": "carousel",
        })}
      >
        {banners.map((blockItem, index) => {
          const featuredProducts = Array.from({ length: 10 }, (_, i) => {
            const name = blockItem?.settings?.[`featured_product_name_${i + 1}`]?.trim?.() || "";
            const url = blockItem?.settings?.[`featured_product_url_${i + 1}`]?.trim?.() || "";
            return name || url ? { name, url } : null;
          }).filter(Boolean);
          const hasFeaturedProductData = featuredProducts.length > 0;

          let imageUrl = blockItem.settings.image?.src ?? blockItem.settings.image;
          if (isMobile) {
            imageUrl = blockItem.settings.mobile_image
              ? blockItem.settings.mobile_image
              : blockItem.settings.image?.src ?? blockItem.settings.image;
          }

          const videoStr = `
            <video
              id="video-${index}"
              muted
              ${videoPlaying[index] || blockItem.settings.should_autoplay ? "autoplay" : ""}
              ${videoPlaying[index] || blockItem.settings.show_controls ? "controls" : ""}
              ${blockItem.settings.should_loop ? "loop" : ""}
              class="object-cover h-full w-full"
              poster="${videoSourcesArray[index]?.videoPoster || ""}"
              preload="metadata"
              playsinline>
              <source src="${videoSourcesArray[index]?.videoUrl || ""}" type="video/mp4"/>
            </video>
          `;

          const VideoContent = () => {
            const isVimeo = videoSourcesArray[index]?.isVimeo;
            
            if (isVimeo) {
              return (
                <div className={`flex lg:h-full w-full ${blockItem.settings.darken_image && "content-over-media"} relative ${section_settings.use_carousel && `h-full`} ${!section_settings.use_carousel && `h-[${section_settings.mobile_height}px]`}`}>
                  <div className="absolute inset-0 w-full h-full aspect-[16/9]">
                    <iframe
                      id={`vimeo-${index}`}
                      src={videoSourcesArray[index]?.videoUrl || ""}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500%] h-[500%] object-cover"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              );
            }
            
            return (
              <div className={`flex lg:h-full w-full ${blockItem.settings.darken_image && "content-over-media"} relative ${section_settings.use_carousel && `h-full`} ${!section_settings.use_carousel && `h-[${section_settings.mobile_height}px]`}`}>
                <span
                  className="w-full h-full"
                  dangerouslySetInnerHTML={{ __html: videoStr }}
                />

                {!videoPlaying[index] && !blockItem.settings.should_autoplay && (
                    <div className="absolute flex items-center justify-center w-full h-full">
                      <button
                          className="relative flex items-center justify-center text-black fill-black bg-[#e5e7eb] rounded-full w-[50px] h-[50px]"
                          onClick={() => handlePlayButtonClick(index)}
                      >
                        <i className="text-black icon icon-play"></i>
                      </button>
                    </div>
                )}
              </div>
            )
          };

          const BannerImageContent = () => {
            // Determine if this is a priority/hero banner
            const isFirstSlide = index === 0;
            const isHeroImage = (section_settings.is_primary || false) && isFirstSlide;

            return (
              <BannerImage 
                blockItem={blockItem}
                section_settings={section_settings}
                isMobile={isMobile}
                isLazy={!isHeroImage}
                isHero={isHeroImage}
                isFirstSlide={isFirstSlide}
              />
            );
          };
          
          return (
            <div key={index} className="relative w-full h-full">
              {hasFeaturedProductData && (
                <div className="sr-only" aria-hidden="true">
                  {featuredProducts.map((product, i) => (
                    <span
                      key={i}
                      name={`featured_product_${i + 1}`}
                      data-product-name={product.name || undefined}
                      data-product-url={product.url || undefined}
                    >
                    </span>
                  ))}
                </div>
              )}

              {blockItem.type === "block_image" && (
                <>
                  {/* Background content - only wrap in anchor if make_banner_clickable is true AND there's only one button */}
                  {blockItem.settings.make_banner_clickable && 
                   blockItem.settings.button_url && 
                   blockItem.settings.button_text && 
                   !blockItem.settings.button_text_2 ? (
                    <a
                      href={blockItem.settings.button_url}
                      onClick={(e) => handleClick(e, blockItem)}
                      className="absolute inset-0 z-[10] block w-full h-full"
                    >
                      <BannerImageContent />
                    </a>
                  ) : (
                    <BannerImageContent />
                  )}

                  {/* Overlay always on top, never wrapped in anchor */}
                  {blockItem.settings.show_overlay && (
                    <div className="absolute inset-0 z-20 pointer-events-none">
                      <div className="pointer-events-auto">
                        <BannerOverlay
                          blockItem={blockItem}
                          section_settings={section_settings}
                          isMobile={isMobile}
                          onClick={(e) => handleClick(e, blockItem)}
                          onSecondButtonClick={(e) => handleSecondButtonClick(e, blockItem)}
                        />
                      </div>
                    </div>
                  )}
                </>
            )}

            {blockItem.type === "block_video" && (
              <>
                {/* Background content - only wrap in anchor if make_banner_clickable is true AND there's only one button */}
                {blockItem.settings.make_banner_clickable && 
                 blockItem.settings.button_url && 
                 blockItem.settings.button_text && 
                 !blockItem.settings.button_text_2 ? (
                  <a
                    href={blockItem.settings.button_url}
                    onClick={(e) => handleClick(e, blockItem)}
                    className="absolute inset-0 z-[10] block w-full h-full"
                  >
                    <VideoContent />
                  </a>
                ) : (
                  <VideoContent />
                )}

                {/* Overlay always on top, never wrapped in anchor */}
                {blockItem.settings.show_overlay && (
                  <div className="absolute inset-0 z-20 pointer-events-none">
                    <div className="pointer-events-auto">
                      <BannerOverlay
                        blockItem={blockItem}
                        section_settings={section_settings}
                        isMobile={isMobile}
                        onClick={(e) => handleClick(e, blockItem)}
                        onSecondButtonClick={(e) => handleSecondButtonClick(e, blockItem)}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Banner;
